/**
 * Admin Network Status Management
 * Manage servers and incidents
 */

import React, { useState, useEffect } from 'react';
import {
  Activity,
  Plus,
  Edit,
  Trash2,
  Server,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Wrench,
} from 'lucide-react';

interface NetworkServer {
  id: number;
  name: string;
  hostname: string;
  location: string | null;
  status: 'operational' | 'degraded' | 'down' | 'maintenance';
  last_check: string | null;
  uptime_percentage: number;
}

interface NetworkIssue {
  id: number;
  title: string;
  description: string;
  type: 'incident' | 'maintenance';
  severity: 'minor' | 'major' | 'critical';
  status: string;
  affected_services: string | null;
  started_at: string;
  resolved_at: string | null;
  scheduled_start: string | null;
  scheduled_end: string | null;
}

export const NetworkStatus: React.FC = () => {
  const [servers, setServers] = useState<NetworkServer[]>([]);
  const [issues, setIssues] = useState<NetworkIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<'servers' | 'issues'>('servers');
  const [filterType, setFilterType] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');

  useEffect(() => {
    loadServers();
    loadIssues();
  }, [filterType, filterStatus]);

  const loadServers = async () => {
    try {
      const response = await fetch('/api/v1/admin/network-status/servers', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setServers(data.data.servers);
      }
    } catch (error) {
      console.error('Failed to load servers:', error);
    }
  };

  const loadIssues = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('type', filterType);
      if (filterStatus) params.append('status', filterStatus);

      const response = await fetch(`/api/v1/admin/network-status/issues?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setIssues(data.data.issues);
      }
    } catch (error) {
      console.error('Failed to load issues:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteServer = async (serverId: number) => {
    if (!confirm('Are you sure you want to delete this server?')) return;

    try {
      const response = await fetch(`/api/v1/admin/network-status/servers/${serverId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        loadServers();
      }
    } catch (error) {
      console.error('Failed to delete server:', error);
    }
  };

  const handleDeleteIssue = async (issueId: number) => {
    if (!confirm('Are you sure you want to delete this issue?')) return;

    try {
      const response = await fetch(`/api/v1/admin/network-status/issues/${issueId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        loadIssues();
      }
    } catch (error) {
      console.error('Failed to delete issue:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'degraded':
        return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case 'down':
        return <XCircle className="w-5 h-5 text-red-400" />;
      case 'maintenance':
        return <Wrench className="w-5 h-5 text-orange-400" />;
      default:
        return <Server className="w-5 h-5 text-slate-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      operational: 'bg-green-900 text-green-300',
      degraded: 'bg-yellow-900 text-yellow-300',
      down: 'bg-red-900 text-red-300',
      maintenance: 'bg-orange-900 text-blue-300',
    };
    return colors[status as keyof typeof colors] || 'bg-slate-700/50 text-slate-300';
  };

  const getSeverityBadge = (severity: string) => {
    const colors = {
      minor: 'bg-yellow-900 text-yellow-300',
      major: 'bg-orange-900 text-orange-300',
      critical: 'bg-red-900 text-red-300',
    };
    return colors[severity as keyof typeof colors] || 'bg-slate-700/50 text-slate-300';
  };

  const overallStatus = servers.length === 0 ? 'operational' :
    servers.some(s => s.status === 'down') ? 'down' :
    servers.some(s => s.status === 'degraded') ? 'degraded' :
    servers.every(s => s.status === 'maintenance') ? 'maintenance' :
    'operational';

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Activity className="w-6 h-6" />
              Network Status
            </h1>
            <p className="text-slate-400 mt-1">
              Monitor servers and manage incidents
            </p>
          </div>
          <button className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New {selectedTab === 'servers' ? 'Server' : 'Issue'}
          </button>
        </div>
      </div>

      {/* Overall Status Card */}
      <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0">
            {getStatusIcon(overallStatus)}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-white mb-1">Overall System Status</h3>
            <p className="text-slate-400">
              {overallStatus === 'operational' && 'All systems operational'}
              {overallStatus === 'degraded' && 'Some systems experiencing issues'}
              {overallStatus === 'down' && 'Service disruption detected'}
              {overallStatus === 'maintenance' && 'Scheduled maintenance in progress'}
            </p>
          </div>
          <span className={`px-4 py-2 rounded-lg font-medium ${getStatusBadge(overallStatus)}`}>
            {overallStatus.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-slate-700/50">
        <button
          onClick={() => setSelectedTab('servers')}
          className={`px-4 py-2 font-medium transition-colors ${
            selectedTab === 'servers'
              ? 'text-orange-400 border-b-2 border-blue-400'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Servers ({servers.length})
        </button>
        <button
          onClick={() => setSelectedTab('issues')}
          className={`px-4 py-2 font-medium transition-colors ${
            selectedTab === 'issues'
              ? 'text-orange-400 border-b-2 border-blue-400'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Issues ({issues.length})
        </button>
      </div>

      {/* Servers Tab */}
      {selectedTab === 'servers' && (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="text-left p-4 text-slate-300 font-medium">Server</th>
                <th className="text-left p-4 text-slate-300 font-medium">Hostname</th>
                <th className="text-left p-4 text-slate-300 font-medium">Location</th>
                <th className="text-left p-4 text-slate-300 font-medium">Status</th>
                <th className="text-left p-4 text-slate-300 font-medium">Uptime</th>
                <th className="text-left p-4 text-slate-300 font-medium">Last Check</th>
                <th className="text-right p-4 text-slate-300 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {servers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-slate-400">
                    No servers configured
                  </td>
                </tr>
              ) : (
                servers.map((server) => (
                  <tr key={server.id} className="hover:bg-slate-700/30">
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-white font-medium">
                        {getStatusIcon(server.status)}
                        {server.name}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-300 text-sm font-mono">
                        {server.hostname}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-400">
                        {server.location || '-'}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-sm ${getStatusBadge(server.status)}`}>
                        {server.status}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-300">
                        {server.uptime_percentage.toFixed(2)}%
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-400 text-sm flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {server.last_check
                          ? new Date(server.last_check).toLocaleString()
                          : 'Never'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        <button
                          className="p-2 text-orange-400 hover:bg-slate-700/50 rounded"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteServer(server.id)}
                          className="p-2 text-red-400 hover:bg-slate-700/50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Issues Tab */}
      {selectedTab === 'issues' && (
        <>
          {/* Filters */}
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 mb-6">
            <div className="flex gap-4">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white"
              >
                <option value="">All Types</option>
                <option value="incident">Incidents</option>
                <option value="maintenance">Maintenance</option>
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white"
              >
                <option value="">All Status</option>
                <option value="investigating">Investigating</option>
                <option value="identified">Identified</option>
                <option value="monitoring">Monitoring</option>
                <option value="resolved">Resolved</option>
                <option value="scheduled">Scheduled</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>

          {/* Issues Table */}
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="text-left p-4 text-slate-300 font-medium">Title</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Type</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Severity</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Status</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Started</th>
                  <th className="text-right p-4 text-slate-300 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-slate-400">
                      Loading...
                    </td>
                  </tr>
                ) : issues.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-slate-400">
                      No issues found
                    </td>
                  </tr>
                ) : (
                  issues.map((issue) => (
                    <tr key={issue.id} className="hover:bg-slate-700/30">
                      <td className="p-4">
                        <div>
                          <div className="text-white font-medium">{issue.title}</div>
                          {issue.affected_services && (
                            <div className="text-sm text-slate-400 mt-1">
                              Affects: {issue.affected_services}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span
                          className={`px-2 py-1 rounded text-sm ${
                            issue.type === 'incident'
                              ? 'bg-red-900 text-red-300'
                              : 'bg-orange-900 text-blue-300'
                          }`}
                        >
                          {issue.type}
                        </span>
                      </td>
                      <td className="p-4">
                        <span
                          className={`px-2 py-1 rounded text-sm ${getSeverityBadge(
                            issue.severity
                          )}`}
                        >
                          {issue.severity}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="px-2 py-1 bg-slate-700/50 text-slate-300 rounded text-sm">
                          {issue.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-slate-300 text-sm">
                          {new Date(issue.started_at).toLocaleString()}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          <button
                            className="p-2 text-orange-400 hover:bg-slate-700/50 rounded"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteIssue(issue.id)}
                            className="p-2 text-red-400 hover:bg-slate-700/50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default NetworkStatus;
